import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { RelatedContent } from '@/components/InternalLinkOptimizer';
import OptimizedImage from '@/components/ui/optimized-image';
import PilgrimsArcadeShowcase from '@/components/PilgrimsArcadeShowcase';
import BibleStudyShowcase from '@/components/BibleStudyShowcase';
import MappaCaeliLearningShowcase from '@/components/MappaCaeliLearningShowcase';
import { BookOpen, Map, User, Clock, Check, PenTool, MessageCircle, Sparkles, FileText, Grid, Palette, Trophy, Crown, CheckCircle, AlertCircle, Heart, TrendingUp, Shield, Zap, Target, BarChart, Gamepad2, Book } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';
import { useConversionTracking } from '@/utils/tracking';
import { useScrollTracking } from '@/hooks/useScrollTracking';
import { useTimeTracking } from '@/hooks/useTimeTracking';
const Index = () => {
  const {
    t
  } = useTranslation();
  const location = useLocation();
  const {
    trackCTAClick
  } = useConversionTracking();

  // Track page engagement
  useScrollTracking('landing_page');
  useTimeTracking('landing_page');
  const homePageStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Mappa Caeli",
    "alternateName": "Catholic Formation for the Whole Family",
    "url": "https://mappa-caeli.com",
    "logo": "https://mappa-caeli.com/assets/mappa-caeli-logo.png",
    "description": "Complete Catholic formation platform with catechism, Bible study, games, and activities for the whole family (ages 7+)",
    "foundingDate": "2024",
    "educationalLevel": "Elementary",
    "audienceType": "Catholic Families",
    "teaches": ["Catholic Catechism", "Traditional Catholic Doctrine", "Saint Pius X Catechism", "Religious Education", "Catholic Family Values"],
    "hasOffering": [{
      "@type": "Course",
      "name": "Traditional Catholic Catechism",
      "description": "Interactive lessons based on Saint Pius X catechism",
      "provider": {
        "@type": "Organization",
        "name": "Mappa Caeli"
      }
    }],
    "sameAs": []
  };
  return <>
      <SEOHead title="Mappa Caeli - Catholic Formation for the Whole Family | Catechism, Bible Study & Games" description="Complete Catholic formation platform: Catechism of Saint Pius X, Bible study with Catholic commentary, 18+ free games, and activities for the whole family. Ages 7 to 70+." keywords="Catholic family formation, Saint Pius X catechism, Catholic Bible study, Catholic games, family faith formation, Catholic homeschool, religious education, Catholic activities" structuredData={homePageStructuredData} />
      <div className="min-h-screen bg-background">
        <Navigation />

    {/* Hero Section - Above the Fold */}
    <section className="relative overflow-hidden bg-gradient-to-br from-background via-muted/50 to-primary/10 py-6 md:py-12 min-h-[500px] md:min-h-[600px] lg:min-h-[650px] lg:py-[30px]">
      <div className="absolute inset-0 bg-parchment-texture opacity-30"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        {/* Hero Background Image - Absolutely positioned with radial gradient mask */}
        <div 
          className="hidden lg:block absolute -right-8 top-0 bottom-0 w-[60%] pointer-events-none z-0"
          style={{
            maskImage: 'radial-gradient(ellipse at 75% center, rgba(0,0,0,1) 35%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.5) 65%, rgba(0,0,0,0) 85%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 75% center, rgba(0,0,0,1) 35%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.5) 65%, rgba(0,0,0,0) 85%)'
          }}
        >
          <OptimizedImage 
            src="/lovable-uploads/tlm_no_bg_altar-2.png" 
            alt="Traditional Latin Mass at the altar" 
            width={900} 
            height={1000} 
            priority={true} 
            className="h-full w-full object-cover object-top opacity-75" 
          />
        </div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-0 items-center h-full relative z-10">
          {/* Left: Value Proposition */}
          <div className="text-center lg:text-left min-h-[400px] lg:min-h-[500px] flex flex-col justify-center">
            {/* Main Headline - Compact */}
            <h1 className="font-medieval text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-2 md:mb-4 leading-tight">
              {t("home.hero.title")}
            </h1>

            {/* Sub-headline */}
            <p className="font-quest text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground mb-3 md:mb-6 leading-relaxed">
              {t("home.hero.subtitle")}
            </p>

            {/* Key Benefits - Checkmarks */}
            <div className="grid sm:grid-cols-2 gap-2 mb-4 md:mb-6 text-left">
              <div className="flex items-start gap-2">
                <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                <span className="font-quest text-sm md:text-base text-foreground">{t("home.hero.benefit1")}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                <span className="font-quest text-sm md:text-base text-foreground">{t("home.hero.benefit2")}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                <span className="font-quest text-sm md:text-base text-foreground">{t("home.hero.benefit3")}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                <span className="font-quest text-sm md:text-base text-foreground">{t("home.hero.benefit4")}</span>
              </div>
            </div>

            {/* Primary CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-3 md:mb-4">
              <Button asChild size="lg" className="quest-button text-base md:text-lg px-6 md:px-8 py-4 md:py-5 h-auto" onClick={() => trackCTAClick('hero', 'Start Learning for $20')}>
                <Link to="/signup">{t("home.hero.primaryCTA")}</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent border-2 border-primary text-primary hover:bg-primary/10 text-base md:text-lg px-6 md:px-8 py-4 md:py-5 h-auto" onClick={() => trackCTAClick('hero', 'Try Free Demo')}>
                <Link to="/demo">{t("home.hero.secondaryCTA")}</Link>
              </Button>
            </div>

            {/* Friction Reducer */}
            <p className="font-quest text-xs md:text-sm text-muted-foreground mb-4">
              {t("home.hero.frictionReducer")}
            </p>

            {/* Personal Family Note - Humanizing Touch */}
            <div className="bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 mb-4 text-left max-w-xl mx-auto lg:mx-0">
              <p className="font-quest text-sm md:text-base text-amber-900 dark:text-amber-100 font-medium mb-1">
                {t("home.hero.familyNote.line1")}
              </p>
              <p className="font-quest text-xs md:text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                {t("home.hero.familyNote.line2")}
              </p>
              <Link to="/about-us" className="font-quest text-xs md:text-sm text-primary hover:underline mt-2 inline-block">
                {t("home.hero.familyNote.learnMore")}
              </Link>
            </div>

            {/* Feature Banners - Above the Fold */}
            <div className="flex flex-col sm:flex-row gap-2 justify-center lg:justify-start">
              <Link to="/pilgrims-arcade" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl hover:scale-105 animate-pulse">
                <Gamepad2 size={18} />
                {t("pilgrimsArcade.heroBanner", "🎮 18 FREE Catholic Games — Play Now!")}
              </Link>
              <Link to="/bible-study" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-bold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                <Book size={18} />
                {t("bibleStudy.heroBanner", "📖 NEW: Catholic Bible Study — Start Reading!")}
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>

    {/* Problem Section - Pain Points */}
    <section className="py-16 bg-gradient-to-br from-muted/30 to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("home.problem.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Pain Point 1 */}
          <div className="quest-card p-6 text-center">
            <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-destructive/40">
              <AlertCircle className="text-destructive" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.problem.pain1Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.problem.pain1Desc")}
            </p>
          </div>

          {/* Pain Point 2 */}
          <div className="quest-card p-6 text-center">
            <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-destructive/40">
              <Clock className="text-destructive" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.problem.pain2Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.problem.pain2Desc")}
            </p>
          </div>

          {/* Pain Point 3 */}
          <div className="quest-card p-6 text-center">
            <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-destructive/40">
              <Heart className="text-destructive" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.problem.pain3Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.problem.pain3Desc")}
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Solution Section */}
    <section className="py-16 bg-gradient-to-r from-primary/5 to-accent/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          {t("home.solution.title")}
        </h2>
        <p className="font-quest text-xl md:text-2xl text-primary font-semibold mb-6">
          {t("home.solution.subtitle")}
        </p>
        <p className="font-quest text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
          {t("home.solution.description")}
        </p>
        
        <div className="mt-8">
          <Button asChild size="lg" className="quest-button text-lg px-8 py-6 h-auto">
            <Link to="/signup">{t("home.hero.primaryCTA")}</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* Pilgrim's Arcade Showcase - FREE GAMES Section */}
    <PilgrimsArcadeShowcase />

    {/* Bible Study Showcase - NEW FEATURE Section */}
    <BibleStudyShowcase />

    {/* Mappa Caeli Learning Showcase - NEW FEATURE Section */}
    <MappaCaeliLearningShowcase />

    {/* Benefits Section - Benefit-Driven */}
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("home.benefits.sectionTitle")}
          </h2>
          <p className="font-quest text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("home.benefits.sectionDesc")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Benefit 1 */}
          <div className="quest-card p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 border-2 border-primary shadow-lg">
              <Map className="text-primary" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.benefits.benefit1Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.benefits.benefit1Desc")}
            </p>
          </div>

          {/* Benefit 2 */}
          <div className="quest-card p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 border-2 border-primary shadow-lg">
              <BookOpen className="text-primary" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.benefits.benefit2Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.benefits.benefit2Desc")}
            </p>
          </div>

          {/* Benefit 3 */}
          <div className="quest-card p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 border-2 border-primary shadow-lg">
              <BarChart className="text-primary" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.benefits.benefit3Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.benefits.benefit3Desc")}
            </p>
          </div>

          {/* Benefit 4 */}
          <div className="quest-card p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 border-2 border-primary shadow-lg">
              <Zap className="text-primary" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.benefits.benefit4Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.benefits.benefit4Desc")}
            </p>
          </div>

          {/* Benefit 5 */}
          <div className="quest-card p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 border-2 border-primary shadow-lg">
              <Shield className="text-primary" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.benefits.benefit5Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.benefits.benefit5Desc")}
            </p>
          </div>

          {/* Benefit 6 */}
          <div className="quest-card p-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 border-2 border-primary shadow-lg">
              <TrendingUp className="text-primary" size={28} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.benefits.benefit6Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.benefits.benefit6Desc")}
            </p>
          </div>
        </div>

        {/* Mid-page CTA */}
        <div className="text-center mt-12">
          <Button asChild size="lg" className="quest-button text-lg px-8 py-6 h-auto">
            <Link to="/signup">{t("home.hero.primaryCTA")}</Link>
          </Button>
        </div>
      </div>
    </section>

    {/* How It Works Section */}
    <section className="py-20 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("home.howItWorks.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Step 1 */}
          <div className="quest-card p-8 text-center relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl border-4 border-background">
              1
            </div>
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 mt-4 border-2 border-primary shadow-lg">
              <User className="text-primary" size={32} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.howItWorks.step1Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.howItWorks.step1Desc")}
            </p>
          </div>

          {/* Step 2 */}
          <div className="quest-card p-8 text-center relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl border-4 border-background">
              2
            </div>
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 mt-4 border-2 border-primary shadow-lg">
              <Map className="text-primary" size={32} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.howItWorks.step2Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.howItWorks.step2Desc")}
            </p>
          </div>

          {/* Step 3 */}
          <div className="quest-card p-8 text-center relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl border-4 border-background">
              3
            </div>
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 mt-4 border-2 border-primary shadow-lg">
              <Trophy className="text-primary" size={32} />
            </div>
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.howItWorks.step3Title")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.howItWorks.step3Desc")}
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Pricing Section */}
    <section className="py-20 bg-gradient-to-r from-primary/5 to-accent/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("home.pricing.title")}
          </h2>
        </div>

        <div className="quest-card p-8 md:p-12 max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-5xl md:text-6xl font-bold text-primary mb-2">
              {t("home.pricing.price")}
            </div>
            <div className="text-xl text-muted-foreground mb-2">
              {t("home.pricing.priceDesc")}
            </div>
            <div className="text-lg text-muted-foreground">
              {t("home.pricing.additionalChild").split(" • ").map((part, idx) => <span key={idx}>
                  {idx === 0 ? <span className="font-bold text-foreground">{part}</span> : part}
                  {idx === 0 && " • "}
                </span>)}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
              <span className="font-quest text-foreground">{t("home.pricing.feature1")}</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
              <span className="font-quest text-foreground">{t("home.pricing.feature2")}</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
              <span className="font-quest text-foreground">{t("home.pricing.feature3")}</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
              <span className="font-quest text-foreground">{t("home.pricing.feature4")}</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
              <span className="font-quest text-foreground">{t("home.pricing.feature5")}</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
              <span className="font-quest text-foreground">{t("home.pricing.feature6")}</span>
            </div>
          </div>

          <div className="text-center mb-6">
            <Button asChild size="lg" className="quest-button text-xl px-12 py-6 h-auto w-full sm:w-auto">
              <Link to="/signup">{t("home.pricing.cta")}</Link>
            </Button>
          </div>

          <div className="bg-muted/30 border-l-4 border-primary rounded p-4 text-center">
            <p className="font-quest text-sm text-muted-foreground">
              <Shield className="inline mr-2" size={16} />
              {t("home.pricing.guarantee")}
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ Section */}
    <section className="py-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("home.faq.title")}
          </h2>
        </div>

        <div className="space-y-6">
          {/* FAQ 1 */}
          <div className="quest-card p-6">
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.faq.q1")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.faq.a1")}
            </p>
          </div>

          {/* FAQ 2 */}
          <div className="quest-card p-6">
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.faq.q2")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.faq.a2")}
            </p>
          </div>

          {/* FAQ 3 */}
          <div className="quest-card p-6">
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.faq.q3")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.faq.a3")}
            </p>
          </div>

          {/* FAQ 4 */}
          <div className="quest-card p-6">
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.faq.q4")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.faq.a4")}
            </p>
          </div>

          {/* FAQ 5 */}
          <div className="quest-card p-6">
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.faq.q5")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.faq.a5")}
            </p>
          </div>

          {/* FAQ 6 */}
          <div className="quest-card p-6">
            <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
              {t("home.faq.q6")}
            </h3>
            <p className="font-quest text-muted-foreground">
              {t("home.faq.a6")}
            </p>
          </div>
        </div>
      </div>
    </section>

      {/* Ask Saint Pius X Section */}
      <section className="py-16 bg-gradient-to-r from-primary/5 to-accent/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="quest-card p-8 text-center">
            <div className="flex flex-col items-center gap-6">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center border-2 border-primary shadow-lg">
                <MessageCircle className="text-primary" size={32} />
              </div>
              <div>
                <h2 className="font-medieval text-2xl md:text-3xl font-bold text-foreground mb-4">
                  {t("home.hero.aiFeature.title")}
                </h2>
                <p className="font-quest text-lg text-muted-foreground mb-6 max-w-2xl">
                  {t("home.hero.aiFeature.description")}
                </p>
              </div>
              <Button asChild size="lg" className="quest-button bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-lg px-8 py-4">
                <Link to="/ask-saint-pius-x" className="flex items-center gap-2">
                  <Sparkles size={20} />
                  {t("home.hero.aiFeature.ctaButton")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Saint of the Day + Trivia Quick Links - Compact version */}
      <section className="py-12 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Saint of the Day */}
            <Link to="/saint-of-the-day" className="quest-card p-6 flex items-center gap-4 group hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center border-2 border-primary shadow-lg group-hover:bg-primary/30 transition-colors">
                <Crown className="text-primary" size={24} />
              </div>
              <div>
                <h3 className="font-medieval text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                  {t("apps.saintOfDay.title")}
                </h3>
                <p className="font-quest text-sm text-muted-foreground">
                  {t("apps.saintOfDay.button")}
                </p>
              </div>
            </Link>

            {/* Catholic Trivia */}
            <Link to="/daily-trivia" className="quest-card p-6 flex items-center gap-4 group hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center border-2 border-primary shadow-lg group-hover:bg-primary/30 transition-colors">
                <Trophy className="text-primary" size={24} />
              </div>
              <div>
                <h3 className="font-medieval text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                  {t("apps.triviaGame.title")}
                </h3>
                <p className="font-quest text-sm text-muted-foreground">
                  {t("apps.triviaGame.button")}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Free Printables CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-medieval text-3xl md:text-4xl font-bold text-foreground mb-6">
              {t("printables.sectionTitle")}
            </h2>
            <p className="font-quest text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("printables.sectionDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Word Search */}
            <div className="quest-card p-6 text-center group hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-primary shadow-lg group-hover:bg-primary/30 transition-colors duration-300">
                <FileText className="text-primary" size={28} />
              </div>
              <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
                {t("printables.wordSearch.title")}
              </h3>
              <p className="font-quest text-muted-foreground mb-4">
                {t("printables.wordSearch.desc")}
              </p>
              <Button asChild className="w-full">
                <Link to="/word-search">{t("printables.wordSearch.button")}</Link>
              </Button>
            </div>

            {/* Crossword */}
            <div className="quest-card p-6 text-center group hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-primary shadow-lg group-hover:bg-primary/30 transition-colors duration-300">
                <Grid className="text-primary" size={28} />
              </div>
              <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
                {t("printables.crossword.title")}
              </h3>
              <p className="font-quest text-muted-foreground mb-4">
                {t("printables.crossword.desc")}
              </p>
              <Button asChild className="w-full">
                <Link to="/crossword">{t("printables.crossword.button")}</Link>
              </Button>
            </div>

            {/* Coloring Page */}
            <div className="quest-card p-6 text-center group hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-primary shadow-lg group-hover:bg-primary/30 transition-colors duration-300">
                <Palette className="text-primary" size={28} />
              </div>
              <h3 className="font-medieval text-xl font-semibold text-foreground mb-3">
                {t("printables.coloring.title")}
              </h3>
              <p className="font-quest text-muted-foreground mb-4">
                {t("printables.coloring.desc")}
              </p>
              <Button asChild className="w-full">
                <Link to="/coloring-page">{t("printables.coloring.button")}</Link>
              </Button>
            </div>
          </div>

          
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="font-medieval text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6">
            {t("home.finalCTA.title")}
          </h2>
          <p className="font-quest text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            {t("home.finalCTA.subtitle")}
          </p>

          <Button asChild size="lg" className="bg-background text-primary hover:bg-background/90 text-base md:text-xl px-6 md:px-12 py-4 md:py-6 h-auto font-bold" onClick={() => trackCTAClick('final_cta', 'Yes, I Want This for My Child')}>
            <Link to="/signup">{t("home.finalCTA.cta")}</Link>
          </Button>
        </div>
      </section>

    <RelatedContent currentPath={location.pathname} />
    <Footer />
    </div>
    </>;
};
export default Index;